import { useMemo } from 'react'
import { Button, Empty, Tree } from 'antd'
import type { DataNode } from 'antd/es/tree'
import type { DocCategory } from './CategoryBrowsePanel'

export type DocCategoryTreeNode = DocCategory & { children?: DocCategoryTreeNode[] }

export const MAX_DOC_CATEGORY_DEPTH = 5

export function docCategoryDepth(path?: string | null): number {
  const p = (path || '').trim()
  if (!p) return 1
  return p.split('/').filter(Boolean).length
}

export function docCategorySubtreeHeight(node: DocCategoryTreeNode): number {
  if (!node.children?.length) return 1
  return 1 + Math.max(...node.children.map(docCategorySubtreeHeight))
}

/** 可选上级的最大层级（含该级）；子树高度为 h 时，上级最深为 5 - h + 1 */
export function maxSelectableParentDepth(subtreeHeight: number): number {
  return Math.max(0, MAX_DOC_CATEGORY_DEPTH - subtreeHeight)
}

type Props = {
  tree: DocCategoryTreeNode[]
  excludeIds?: Set<number>
  value?: number | null
  onChange?: (parentId: number | null) => void
  /** 当前编辑节点子树高度，用于限制可选上级（新建传 1） */
  subtreeHeight?: number
}

export function DocCategoryParentBrowsePanel({
  tree,
  excludeIds,
  value,
  onChange,
  subtreeHeight = 1,
}: Props) {
  const maxParentDepth = maxSelectableParentDepth(subtreeHeight)
  const filtered = useMemo(() => filterTree(tree, excludeIds ?? new Set()), [tree, excludeIds])
  const treeData = useMemo(
    () => toAntTree(filtered, maxParentDepth),
    [filtered, maxParentDepth],
  )
  const topLevelAllowed = subtreeHeight <= MAX_DOC_CATEGORY_DEPTH

  if (!treeData.length) {
    return (
      <div>
        {topLevelAllowed ? (
          <Button type="link" size="small" style={{ padding: 0, marginBottom: 8 }} onClick={() => onChange?.(null)}>
            设为顶级类型
          </Button>
        ) : null}
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可选上级类型" />
      </div>
    )
  }

  return (
    <div>
      {topLevelAllowed ? (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, marginBottom: 8 }}
          onClick={() => onChange?.(null)}
        >
          设为顶级类型
        </Button>
      ) : (
        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
          当前子树层级已达上限，不能设为顶级类型
        </div>
      )}
      <Tree
        blockNode
        defaultExpandAll
        treeData={treeData}
        selectedKeys={value != null ? [value] : []}
        onSelect={(keys, info) => {
          if (info.node.disabled) return
          const id = keys[0]
          if (id == null) return
          onChange?.(typeof id === 'number' ? id : Number(id))
        }}
      />
    </div>
  )
}

function filterTree(nodes: DocCategoryTreeNode[], exclude: Set<number>): DocCategoryTreeNode[] {
  const out: DocCategoryTreeNode[] = []
  for (const node of nodes) {
    if (exclude.has(node.id)) continue
    const children = node.children?.length ? filterTree(node.children, exclude) : undefined
    out.push({ ...node, children: children?.length ? children : undefined })
  }
  return out
}

function toAntTree(nodes: DocCategoryTreeNode[], maxParentDepth: number): DataNode[] {
  return nodes.map((node) => {
    const depth = docCategoryDepth(node.path)
    const disabled = depth > maxParentDepth
    return {
      key: node.id,
      title: disabled ? `${node.path || node.name}（已达层级上限）` : node.path || node.name,
      disabled,
      children: node.children?.length ? toAntTree(node.children, maxParentDepth) : undefined,
    }
  })
}

export function flattenDocCategoryTree(nodes: DocCategoryTreeNode[]): DocCategoryTreeNode[] {
  const out: DocCategoryTreeNode[] = []
  const walk = (list: DocCategoryTreeNode[]) => {
    for (const node of list) {
      out.push(node)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(nodes)
  return out
}

export function collectDocCategoryDescendantIds(node: DocCategoryTreeNode): number[] {
  const out: number[] = []
  const walk = (list?: DocCategoryTreeNode[]) => {
    for (const child of list || []) {
      out.push(child.id)
      walk(child.children)
    }
  }
  walk(node.children)
  return out
}
